// Adapter: unified backend SearchResponse {columns, rows} -> legacy
// EntityRow[] that TablePanel + cell renderers consume.
//
// row.data is keyed by `column.name` (matches the unified RowCell map's
// natural keying). The write path reads SchemaFact.provenance from the
// columns array directly and dispatches off it.
//
// This is the only adapter the application still needs; the schema-side
// `/rms/schema/{kind}` endpoint already returns the legacy RmsSchema
// shape directly. The previous schema-side helpers (columnToLegacyFact,
// unifiedToLegacySchema, isUnifiedSchemaResponse) were dead code and
// were removed when this file replaced legacyAdapter.ts.

import type {
  EntityCellValue,
  EntityKind,
  EntityRow,
  Provenance,
  RowCell,
  RowVersions,
  UnifiedRow,
  UnifiedSearchResponse,
} from "../Dashboard/types";
import { inFamilyOrder, VERSIONS_COLUMN } from "./versionsColumn";
import { entityKindMap } from "../Dashboard/utils";

// Every EntityKind, as runtime values. Read off the kind metadata map — a
// total Record<EntityKind, …> — rather than restated, so a kind added to the
// union is recognised here immediately. A kind missing from this list made
// activityTargetKind return null for its rows, which silently dropped the
// per-attribute renderer on the activity grid's Fact cells.
const ENTITY_KINDS: readonly string[] = Object.keys(entityKindMap);

// The activity row's target kind, when it names an RMS entity. Bootstrap
// schemas are keyed by EntityKind, so non-entity targets ("document") and
// deleted targets (no kind at all) return null and the fact cell falls back
// to inferring its renderer from scalar_type.
function activityTargetKind(row: UnifiedRow): EntityKind | null {
  const kind = row.data["Target Kind"]?.value?.scalar;
  return typeof kind === "string" && ENTITY_KINDS.includes(kind)
    ? (kind as EntityKind)
    : null;
}

// The RMS interactions Name cell renders always-on badges — a group-note
// icon, a private lock, and a provenance marker for document-derived rows
// (Dashboard/Components/NoteKindBadge + PrivateBadge + SourceBadge) —
// independent of which columns are visible. They are row metadata, not
// columns: the backend keeps them in the response even when their columns
// aren't requested (#10653, #10554), but the descriptor-driven row-data
// build below only copies cells whose column is present, so it would drop
// them. Carry them through explicitly. Absent on non-interaction rows, where
// `row.data` has no such key and nothing is added. A meeting's state is the
// Status column's (#12394, round 15), not a badge.
const BADGE_BACKING_CELLS = ["Note Kind", "Share Status", "Source"] as const;

// Duck-typed guard — the response shape is plain JSON.
export function isUnifiedSearchResponse(
  payload: unknown
): payload is UnifiedSearchResponse {
  if (payload === null || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return Array.isArray(p.columns) && Array.isArray(p.rows);
}

// Pull the legacy `EntityCellValue` out of a `RowCell`. CellValue is the
// shape `{scalar?, entity?, document?}`; legacy `EntityCellValue` is
// `FactValue | FactValue[] | null`. Today's renderer accepts both forms
// directly, so we mostly just hand off the shape unchanged.
//
// `isArrayColumn` tells us whether the renderer expects an array — for
// is_array columns whose cell is empty/null on the backend, we must emit
// `[]` rather than the wrapped scalar object, otherwise the array-branch
// renderer (cellRenderers `values.map`) crashes with TypeError on the
// non-array shape.
function rowCellToLegacy(
  cell: RowCell | undefined,
  isArrayColumn: boolean
): EntityCellValue | null {
  if (cell === undefined || cell === null) return isArrayColumn ? [] : null;
  const v = cell.value;
  // Arrays come back as `{scalar: [...legacy FactValue list]}` from the
  // backend's RMS list-fact aggregation. Unwrap so the renderer sees a
  // plain array. Gated on `isArrayColumn` because hierarchical non-array
  // columns also store an array in `scalar` — the path itself, e.g.
  // `["Asset Class", "Private Markets", "Private Equity"]` — and that
  // must reach the renderer wrapped as a single FactValue, not unwrapped
  // as a FactValue[]. Without the gate, hierarchical scalar cells render
  // blank and the autofill action mistakes them for non-empty (#9572).
  if (isArrayColumn && Array.isArray(v.scalar))
    return v.scalar as EntityCellValue;
  // Empty cell on an array column — emit `[]` so the renderer's array
  // branch (`values.length === 0`) returns the em-dash placeholder
  // instead of trying to .map() the scalar wrapper. Production hits this
  // for every empty array fact (e.g. an unfilled Tags / Funds column).
  if (
    isArrayColumn &&
    (v.scalar === null || v.scalar === undefined) &&
    !v.entity &&
    !v.document
  ) {
    return [];
  }
  // Composite scalar+entity gets handed to the renderer as-is — legacy
  // FactValue already supports both fields populated simultaneously.
  // Autofill provenance (query_id / workspace_id) is carried over so the
  // right-click "Source" entry in CellRangeContextMenu can render for
  // scalar AI-filled facts (issue #9534). Array facts already round-trip
  // these via the per-element FactValue list above.
  return {
    scalar: v.scalar ?? null,
    // A numberlike fact's range end and note (#10991) ride beside the number;
    // the grid cell renders the range and the note from these.
    scalar_end: v.scalar_end ?? null,
    scalar_note: v.scalar_note ?? null,
    entity: v.entity ?? null,
    document: v.document ?? null,
    polymorphic: extractPolymorphic(cell),
    staging: cell.override?.staging ?? undefined,
    queryId: v.query_id ?? undefined,
    workspaceId: v.workspace_id ?? undefined,
    // Chat/agent-write provenance (#11499) travels the same way, so the
    // right-click Source entry can link a scalar agent-written fact back to
    // the conversation that set it (#11677). The wire carries it as one
    // nested object (null on the overwhelming majority of cells); flatten it
    // onto the value so both Source surfaces read the same field names.
    // Array facts carry their own flat copy per element via the list above.
    sourceConversationId: v.source?.conversation_id ?? undefined,
    sourceTurnId: v.source?.turn_id ?? undefined,
    sourceViewable: v.source?.viewable ?? undefined,
    sourceAuthorizedBy: v.source?.authorized_by ?? undefined,
    sourceProduct: v.source?.product ?? undefined,
    sourceAgentId: v.source?.agent_id ?? undefined,
    sourceRunId: v.source?.run_id ?? undefined,
    sourceSessionId: v.source?.session_id ?? undefined,
    sourceDocumentId: v.source?.document_id ?? undefined,
    sourceDocumentName: v.source?.document_name ?? undefined,
    sourcePageNum: v.source?.page_num ?? undefined,
    sourceInteractionId: v.source?.interaction_id ?? undefined,
  } as unknown as EntityCellValue;
}

// Activity-grid Target / Source cells. The backend emits the value in
// `cell.value.entity` / `cell.value.document` / `cell.value.scalar`
// (with override.ref pointing at the interaction row for interaction
// targets). The grid's polymorphic-cell renderer reads `value.polymorphic`
// as a `PolymorphicCellValue` ({kind, id, display}); reshape here.
function extractPolymorphic(
  cell: RowCell
): { kind: string; id: string; display: string | null } | undefined {
  // Only polymorphic columns set override.type per row.
  if (!cell.override?.type) return undefined;
  const v = cell.value;
  if (v?.entity) {
    return { kind: v.entity.kind, id: v.entity.id, display: v.entity.name };
  }
  if (v?.document) {
    return {
      kind: "document",
      id: v.document.id,
      display: v.document.filename,
    };
  }
  // Interaction case: scalar=<title> + override.ref={row_kind, row_id}.
  const ref = cell.override.ref;
  if (ref) {
    return {
      kind: ref.row_kind,
      id: ref.row_id,
      display: typeof v?.scalar === "string" ? v.scalar : null,
    };
  }
  // Deleted target restored from the audit snapshot (#10775): scalar
  // display with deliberately no ref — the referenced row is hard-deleted,
  // so there is nothing to navigate to. `deleted_kind` carries the kind
  // for the tombstone's icon; the `deleted` flag routes PolymorphicCell
  // to its grayed-out non-navigable branch.
  const deletedKind = cell.override.deleted_kind;
  if (deletedKind && typeof v?.scalar === "string" && v.scalar) {
    return { kind: deletedKind, id: "", display: v.scalar, deleted: true };
  }
  return undefined;
}

// Build the per-cell provenance override map for one unified row, keyed by
// `column.name`. Only populated for cells whose RowCell carries an
// `override.provenance` — the backend emits this when a column's
// editability/write-routing must vary per row (document_review: a doc's
// Note type is `derived` while an interaction's is `interaction_field`).
// Returns undefined when no cell carries an override, so EntityRow.data
// stays the sole carrier for every existing view and the column-level
// provenance path is untouched.
function unifiedRowToCellProvenance(
  row: UnifiedRow,
  columns: UnifiedSearchResponse["columns"]
): Record<string, Provenance> | undefined {
  let map: Record<string, Provenance> | undefined;
  for (const col of columns) {
    const prov = row.data[col.name]?.override?.provenance;
    if (prov) {
      if (map === undefined) map = {};
      map[col.name] = prov;
    }
  }
  return map;
}

// Whether a cell that arrives without a descriptor is a list column's cell.
// A list column sends its values as a list of FactValue objects on `scalar`;
// a hierarchical column sends its one path as a list of strings there, which
// is a single value (#9572). Only a grouped cell is read this way: every
// described column takes its array-ness from its descriptor.
function isFactValueList(cell: RowCell): boolean {
  const scalar: unknown = cell.value?.scalar;
  return (
    Array.isArray(scalar) &&
    scalar.every(
      (item: unknown) =>
        typeof item === "object" && item !== null && !Array.isArray(item)
    )
  );
}

// Translate one unified row into a legacy `EntityRow.data` dict keyed by
// `column.name` (matches both the unified `RowCell` map and the
// post-migration `SchemaFact.id`).
function unifiedRowToLegacyData(
  row: UnifiedRow,
  columns: UnifiedSearchResponse["columns"],
  groupBy: readonly string[],
  companionCells: readonly string[]
): Record<string, EntityCellValue | null> {
  const data: Record<string, EntityCellValue | null> = {};
  for (const col of columns) {
    // Column is is_array if any of its payload specs says so. All three must
    // be consulted: `Grouped Documents` carries its array-ness in
    // `document_ref` alone, and checking only scalar/entity_ref left its cell
    // wrapped, which crashed the exporter's array branch (#11126).
    const isArrayColumn =
      col.type?.scalar?.is_array === true ||
      col.type?.entity_ref?.is_array === true ||
      col.type?.document_ref?.is_array === true;
    data[col.name] = rowCellToLegacy(row.data[col.name], isArrayColumn);
  }
  // Carry the always-on badge cells through even when their columns aren't in
  // the scoped descriptor set, so the Name-cell signifiers keep rendering
  // (#10653). Both are scalar cells; skip any already added as a visible
  // column, and any row (non-interaction) that doesn't carry them.
  for (const name of [...BADGE_BACKING_CELLS, ...companionCells]) {
    if (data[name] === undefined && row.data[name] !== undefined) {
      data[name] = rowCellToLegacy(row.data[name], false);
    }
  }
  // Grouped columns too: the grid labels each group from the first row's
  // cell of the grouped column, and a grouped column need not be visible, so
  // the search sends its cell without a descriptor (#13305).
  for (const name of groupBy) {
    const cell = row.data[name];
    if (data[name] === undefined && cell !== undefined) {
      data[name] = rowCellToLegacy(cell, isFactValueList(cell));
    }
  }
  // Activity-log extras: copy hack fields into the legacy data dict so the
  // existing FactChangesModal and ActivityFactEntriesCell continue to read
  // them via row.data[...] without changes.
  if (row.activity_hack) {
    const hack = row.activity_hack;
    if (hack.fact_entries && hack.fact_entries.length > 0) {
      // ActivityFactEntriesCell and FactChangesModal read `fact_entries` off
      // the *cell value* (row.data["Fact"]), not off a side channel — parking
      // the hack under its own key left both unreachable, which is half of
      // why the grid showed no values (#10817). Merge onto the Fact cell.
      // "Fact" carries the attribute's name and uses the entries only to
      // open the change-detail modal; "Fact Value" is valueless so the
      // entries render inline as the values themselves.
      const targetKind = activityTargetKind(row);
      for (const name of ["Fact", "Fact Value"]) {
        const cell = data[name];
        if (cell && !Array.isArray(cell)) {
          cell.fact_entries = hack.fact_entries;
          // Per-fact SchemaFact lookup is keyed by EntityKind; non-entity
          // targets (documents, interactions) pass null and fall back to
          // scalar_type inference.
          cell.activity_target_kind = targetKind;
        }
      }
    }
    if (hack.details_before !== undefined)
      data.__details_before__ = hack.details_before;
    if (hack.details_after !== undefined)
      data.__details_after__ = hack.details_after;
    if (hack.review_id !== undefined)
      data.__review_id__ = hack.review_id as unknown as EntityCellValue;
    if (hack.approved_by_display !== undefined)
      data.__approved_by__ =
        hack.approved_by_display as unknown as EntityCellValue;
    if (hack.approved_at !== undefined)
      data.__approved_at__ = hack.approved_at as unknown as EntityCellValue;
    if (hack.bulk_operation_id !== undefined)
      data.__bulk_operation_id__ =
        hack.bulk_operation_id as unknown as EntityCellValue;
    if (hack.llm_reasoning !== undefined)
      data.__llm_reasoning__ = hack.llm_reasoning as unknown as EntityCellValue;
  }
  return data;
}

export function unifiedToLegacyRows(
  payload: UnifiedSearchResponse,
  // The request's `group_by`: those columns' cells are kept on each row
  // whether or not the response describes them.
  groupBy: readonly string[] = [],
  // A process view's companion cells (`ProcessView.companionCells`), carried
  // through like the badge cells.
  companionCells: readonly string[] = []
): EntityRow[] {
  // Grouped on Versions, a row that names a family sits in its band (#13682).
  const banded = groupBy.includes(VERSIONS_COLUMN);
  const rows = payload.rows.map(
    (row): EntityRow => ({
      id: row.id,
      keyset_id: row.keyset_id,
      name: row.name,
      staging: row.staging ?? false,
      group_rank: row.group_rank ?? undefined,
      group_size: row.group_size ?? undefined,
      group_value: row.group_value ?? undefined,
      subgroup_value: row.subgroup_value ?? undefined,
      // Legacy EntityRow.data is typed as Record<string, EntityCellValue>
      // (non-nullable) but the existing renderers all handle null/undefined
      // values; the cell-emptiness check uses `isCellValueEmpty` rather than
      // a structural type guard. Cast at the boundary.
      data: unifiedRowToLegacyData(
        row,
        payload.columns,
        groupBy,
        companionCells
      ) as Record<string, EntityCellValue>,
      cellProvenance: unifiedRowToCellProvenance(row, payload.columns),
      // Carry the polymorphic kind through so the document-review Name cell
      // can pick the right kind icon and open the right viewer. Absent on every
      // other view (the backend only populates it for document_review).
      object_kind: row.object_kind ?? undefined,
      // Carry the row's source through so the transactions grid can show its
      // edit/delete kebab on manual rows only. Absent on every other view.
      source: row.source ?? undefined,
      versions: rowVersions(row.lineage_id, payload.lineages, banded),
    })
  );
  return inFamilyOrder(rows);
}

/** The family a row names, resolved against the response's side map, where
 * each family is sent once however many rows it has (#13682). */
function rowVersions(
  lineageId: number | null | undefined,
  lineages: UnifiedSearchResponse["lineages"],
  banded: boolean
): RowVersions | undefined {
  if (lineageId == null) return undefined;
  return {
    family: lineageId,
    lineage: lineages?.[String(lineageId)] ?? null,
    banded,
  };
}
